/**
 * Hover highlighting utilities for mapping visualization
 */

import { ColumnMapping } from '../components/types';

export type HighlightElementType = 'column' | 'field' | 'metadata';

export interface HighlightedElement {
  type: HighlightElementType;
  name: string;
}

/**
 * Calculate element opacity based on hover state and relationships
 */
export const getElementOpacity = (
  elementType: HighlightElementType,
  elementName: string,
  hoveredElement: HighlightedElement | null,
  columnMappings: ColumnMapping[],
  fileId: string
): number => {
  // If no form is selected, disable hover highlighting
  if (!columnMappings.length) return 1;

  if (!hoveredElement) return 1; // No hover, full opacity

  // If hovering over this same element, full opacity
  if (
    hoveredElement.type === elementType &&
    hoveredElement.name === elementName
  ) {
    return 1;
  }

  // Check if this element is related to the hovered element
  if (hoveredElement.type === 'column') {
    // If hovering over a column, check what it's mapped to
    const columnMapping = columnMappings.find(
      m => m.fileId === fileId && m.columnName === hoveredElement.name
    );
    // Consider 'ignored' mappings as no mapping
    if (columnMapping && columnMapping.mappingType !== 'ignored') {
      if (
        columnMapping.mappingType === 'formField' &&
        elementType === 'field'
      ) {
        return columnMapping.targetField === elementName ? 1 : 0.3;
      } else if (
        columnMapping.mappingType === 'metadata' &&
        elementType === 'metadata'
      ) {
        return columnMapping.metadataType?.type === elementName ? 1 : 0.3;
      }
    } else if (!columnMapping && elementType === 'field') {
      // Check automatic name matching only for truly unmapped columns (not ignored)
      const nameMatch =
        hoveredElement.name.toLowerCase() === elementName.toLowerCase();
      return nameMatch ? 1 : 0.3;
    }
  } else if (hoveredElement.type === 'field') {
    // If hovering over a field, find which column is mapped to it
    if (elementType === 'column') {
      const columnMapping = columnMappings.find(
        m => m.fileId === fileId && m.columnName === elementName
      );

      // Only check explicit mapping - no automatic matching for hover
      const explicitMatch =
        columnMapping &&
        columnMapping.mappingType === 'formField' &&
        columnMapping.targetField === hoveredElement.name;

      return explicitMatch ? 1 : 0.3;
    }
  } else if (hoveredElement.type === 'metadata') {
    // If hovering over metadata, find which column is mapped to it
    if (elementType === 'column') {
      const columnMapping = columnMappings.find(
        m => m.fileId === fileId && m.columnName === elementName
      );
      // Consider 'ignored' mappings as no mapping
      return columnMapping &&
        columnMapping.mappingType === 'metadata' &&
        columnMapping.metadataType?.type === hoveredElement.name
        ? 1
        : 0.3;
    }
  }

  return 0.3; // Not related, dimmed
};

/**
 * Check if element is currently highlighted
 */
export const isElementHighlighted = (
  elementType: HighlightElementType,
  elementName: string,
  hoveredElement: HighlightedElement | null
): boolean => {
  return (
    getElementOpacity(elementType, elementName, hoveredElement, [], '') === 1
  );
};
